import React from 'react';
import { Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faMobileScreen, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { PaymentTerminalView } from '../../../../../services/credit-payments/credit-payments.service';
import type { QuickChargeTender } from '../../../hooks/useQuickCharge';
import { Button } from '../../../../../components/Button';
import classes from './quick-charge-method-modal.module.scss';

type QuickChargeMethodModalProps = {
  step: 'tender' | 'credit' | 'closed';
  terminals: PaymentTerminalView[];
  isTapOnPhoneAvailable: boolean;
  onCash: () => void;
  onCredit: () => void;
  onCreditMethod: (tender: Exclude<QuickChargeTender, { kind: 'cash' }>) => void;
  onBack: () => void;
  onClose: () => void;
};

export const QuickChargeMethodModal: React.FC<QuickChargeMethodModalProps> = ({
  step,
  terminals,
  isTapOnPhoneAvailable,
  onCash,
  onCredit,
  onCreditMethod,
  onBack,
  onClose,
}) => {
  const pinpads = terminals.flatMap((terminal) => terminal.pinpads.map((pinpad) => ({ terminal, pinpad })));
  const manualTarget = pinpads[0] ?? null;

  return (
    <Modal
      opened={step === 'tender' || step === 'credit'}
      onClose={onClose}
      centered
      dir="rtl"
      radius="lg"
      size={560}
      title={step === 'credit' ? 'בחר אמצעי אשראי' : 'בחר סוג תשלום'}
      classNames={{ title: classes.title }}
      closeButtonProps={{ 'data-testid': 'quick-charge-method-close' } as Record<string, string>}
    >
      <div className={classes.options} data-testid={`quick-charge-${step}-choices`}>
        {step === 'tender' ? (
          <>
            <Button variant="save" className={classes.option} onClick={onCredit} dataTestId="quick-charge-tender-credit">
              <FontAwesomeIcon icon={faCreditCard as IconProp} />
              <span>אשראי</span>
            </Button>
            <Button variant="save" className={classes.option} onClick={onCash} dataTestId="quick-charge-tender-cash">
              <FontAwesomeIcon icon={faMoneyBillWave as IconProp} />
              <span>מזומן</span>
            </Button>
          </>
        ) : (
          <>
            {pinpads.map(({ terminal, pinpad }) => (
              <Button
                key={`${terminal.id}:${pinpad.pinpadId}`}
                variant="save"
                className={classes.option}
                onClick={() => onCreditMethod({ kind: 'pinpad', terminalId: terminal.id, pinpadId: pinpad.pinpadId })}
                dataTestId={`quick-charge-credit-pinpad-${pinpad.pinpadId}`}
              >
                <FontAwesomeIcon icon={faCreditCard as IconProp} />
                <span>{pinpad.label ?? terminal.name}</span>
              </Button>
            ))}
            {isTapOnPhoneAvailable ? (
              <Button variant="save" className={classes.option} onClick={() => onCreditMethod({ kind: 'top' })} dataTestId="quick-charge-credit-device">
                <FontAwesomeIcon icon={faMobileScreen as IconProp} />
                <span>מכשיר</span>
              </Button>
            ) : null}
            {manualTarget ? (
              <Button
                variant="save"
                className={classes.option}
                onClick={() => onCreditMethod({
                  kind: 'manual',
                  terminalId: manualTarget.terminal.id,
                  pinpadId: manualTarget.pinpad.pinpadId,
                })}
                dataTestId="quick-charge-credit-mobile"
              >
                <FontAwesomeIcon icon={faCreditCard as IconProp} />
                <span>אשראי נייד</span>
              </Button>
            ) : null}
          </>
        )}
      </div>
      {step === 'credit' ? (
        <Button variant="cancel" fullWidth onClick={onBack} dataTestId="quick-charge-method-back">חזרה</Button>
      ) : null}
    </Modal>
  );
};